import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { proceedingsAPI } from '@/services/proceedingsService';

const initialForm = {
  title: '',
  author: '',
  category: 'academic',
  publish_date: '',
  summary: '',
  image_url: '',
  pdf_url: '',
  is_featured: false,
  is_active: true,
  display_order: 0,
};

const AcademicArticlesAdminPage = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const sorted = useMemo(() => [...items].sort((a, b) => (b.is_active - a.is_active) || (b.is_featured - a.is_featured) || (a.display_order - b.display_order)), [items]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await proceedingsAPI.listAdmin();
      const all = res.data || [];
      setItems(all.filter((item) => item.category === 'academic'));
    } catch (error) {
      toast({ title: 'โหลดข้อมูลไม่สำเร็จ', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setIsOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...initialForm, ...item, category: 'academic', publish_date: item.publish_date ? String(item.publish_date).slice(0, 10) : '' });
    setIsOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, category: 'academic', display_order: Number(form.display_order) || 0, is_featured: !!form.is_featured, is_active: !!form.is_active, publish_date: form.publish_date || null };
    try {
      if (editing) {
        await proceedingsAPI.update(editing.id, payload);
        toast({ title: 'อัปเดตบทความวิชาการสำเร็จ' });
      } else {
        await proceedingsAPI.create(payload);
        toast({ title: 'เพิ่มบทความวิชาการสำเร็จ' });
      }
      setIsOpen(false);
      loadData();
    } catch (error) {
      toast({ title: 'บันทึกไม่สำเร็จ', description: error.message, variant: 'destructive' });
    }
  };

  const onDelete = async () => {
    if (!deleting) return;
    try {
      await proceedingsAPI.remove(deleting.id);
      toast({ title: 'ลบข้อมูลสำเร็จ' });
      setDeleting(null);
      loadData();
    } catch (error) {
      toast({ title: 'ลบไม่สำเร็จ', description: error.message, variant: 'destructive' });
    }
  };

  const uploadSingleFile = async (file) => {
    const body = new FormData();
    body.append('file', file);
    const res = await fetch('/api/upload', { method: 'POST', body });
    const data = await res.json();
    if (!res.ok || !data?.success || !data?.fileUrl) throw new Error(data?.message || data?.error || 'อัปโหลดไฟล์ไม่สำเร็จ');
    return data.fileUrl;
  };

  return (
    <div className="space-y-6 admin-panel" data-admin="true">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">จัดการบทความวิชาการ</h1>
          <p className="text-gray-600 mt-1">เพิ่ม แก้ไข ลบ รายการสำหรับหน้า /academic-articles</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="add-button-gradient" onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> เพิ่มบทความวิชาการ</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{editing ? 'แก้ไขบทความวิชาการ' : 'เพิ่มบทความวิชาการ'}</DialogTitle>
              <DialogDescription>กรอกข้อมูลและอัปโหลดไฟล์ PDF ได้ทันที</DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="หัวข้อ" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                <Input placeholder="ผู้เขียน/หน่วยงาน" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} required />
                <Input type="date" value={form.publish_date} onChange={(e) => setForm({ ...form, publish_date: e.target.value })} />
                <Input type="number" placeholder="ลำดับแสดงผล" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} />
                <div className="space-y-2">
                  <Input placeholder="URL รูปภาพ (image_url)" value={form.image_url || ''} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
                  <input id="academic-image-upload" type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    try { setUploadingImage(true); const fileUrl = await uploadSingleFile(file); setForm((prev) => ({ ...prev, image_url: fileUrl })); }
                    catch (err) { toast({ title: 'อัปโหลดรูปไม่สำเร็จ', description: err.message, variant: 'destructive' }); }
                    finally { setUploadingImage(false); e.target.value = ''; }
                  }} />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('academic-image-upload')?.click()} disabled={uploadingImage}>
                    {uploadingImage ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    {uploadingImage ? 'กำลังอัปโหลดรูป...' : 'อัปโหลดรูปภาพ'}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Input placeholder="URL PDF (pdf_url)" value={form.pdf_url || ''} onChange={(e) => setForm({ ...form, pdf_url: e.target.value })} />
                  <input id="academic-pdf-upload" type="file" accept="application/pdf" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    try { setUploadingPdf(true); const fileUrl = await uploadSingleFile(file); setForm((prev) => ({ ...prev, pdf_url: fileUrl })); }
                    catch (err) { toast({ title: 'อัปโหลด PDF ไม่สำเร็จ', description: err.message, variant: 'destructive' }); }
                    finally { setUploadingPdf(false); e.target.value = ''; }
                  }} />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('academic-pdf-upload')?.click()} disabled={uploadingPdf}>
                    {uploadingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    {uploadingPdf ? 'กำลังอัปโหลด PDF...' : 'อัปโหลด PDF'}
                  </Button>
                </div>
                <div className="flex items-center gap-4 md:col-span-2">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
                </div>
              </div>
              <Textarea placeholder="สรุป (summary)" value={form.summary || ''} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>ยกเลิก</Button>
                <Button type="submit">{editing ? 'บันทึกการแก้ไข' : 'เพิ่มข้อมูล'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">กำลังโหลดข้อมูล...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr>
                  <th className="table-header-custom">หัวข้อ</th>
                  <th className="table-header-custom">ผู้เขียน</th>
                  <th className="table-header-custom">วันที่</th>
                  <th className="table-header-custom">สถานะ</th>
                  <th className="table-header-custom text-right">จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50">
                    <td className="table-cell-custom max-w-[300px] truncate">{item.title}</td>
                    <td className="table-cell-custom">{item.author}</td>
                    <td className="table-cell-custom">{item.publish_date ? String(item.publish_date).slice(0, 10) : '-'}</td>
                    <td className="table-cell-custom"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{item.is_active ? 'active' : 'inactive'}</span></td>
                    <td className="table-cell-custom">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(item)}><Edit className="w-4 h-4 mr-1" />แก้ไข</Button>
                        <Button variant="destructive" size="sm" onClick={() => setDeleting(item)}><Trash2 className="w-4 h-4 mr-1" />ลบ</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบ</AlertDialogTitle>
            <AlertDialogDescription>คุณต้องการลบรายการ "{deleting?.title}" ใช่หรือไม่?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">ลบ</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AcademicArticlesAdminPage;
